import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import {
  Send,
  Sparkles,
  ArrowLeft,
  Loader2,
  Bot,
  User,
  AlertCircle,
  BookOpen,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { useShakespeare, type ChatMessage, type Model } from '@/hooks/useShakespeare';
import { getLessonById, CURRICULUM } from '@/lib/curriculum';
import { cn } from '@/lib/utils';

interface DisplayMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

function ModelSelector({
  onSelect,
  selected,
}: {
  onSelect: (modelId: string) => void;
  selected: string;
}) {
  const { getAvailableModels } = useShakespeare();
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchModels = async () => {
      setLoading(true);
      try {
        const response = await getAvailableModels();
        const sorted = response.data.sort((a, b) => {
          const costA = parseFloat(a.pricing.prompt) + parseFloat(a.pricing.completion);
          const costB = parseFloat(b.pricing.prompt) + parseFloat(b.pricing.completion);
          return costA - costB;
        });
        setModels(sorted);
        if (sorted.length > 0 && !selected) {
          const cheapest = sorted[0];
          onSelect(cheapest.id);
        }
      } catch {
        // Model fetch failed
      } finally {
        setLoading(false);
      }
    };
    fetchModels();
  }, [getAvailableModels, onSelect, selected]);

  if (loading) {
    return <Skeleton className="h-9 w-40 rounded-lg" />;
  }

  return (
    <select
      value={selected}
      onChange={(e) => onSelect(e.target.value)}
      className="rounded-lg border bg-white px-3 py-2 text-xs font-medium text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300"
    >
      {models.map((m, i) => {
        const total = parseFloat(m.pricing.prompt) + parseFloat(m.pricing.completion);
        return (
          <option key={m.id} value={m.id}>
            {m.name} {i === 0 ? '(Cheapest)' : `$${total.toFixed(6)}/tok`}
          </option>
        );
      })}
    </select>
  );
}

const ChatPage = () => {
  const { moduleId, lessonId } = useParams<{ moduleId?: string; lessonId?: string }>();
  const { isAuthenticated, isLoading: apiLoading, error, sendStreamingMessage, clearError } = useShakespeare();

  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const lessonContext = lessonId && moduleId
    ? getLessonById(moduleId, lessonId)
    : undefined;

  useSeoMeta({
    title: lessonContext
      ? `AI Tutor — ${lessonContext.lesson?.title} — Reality Architect`
      : 'AI Tutor — Reality Architect',
    description: 'Discuss the curriculum with an AI tutor',
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.style.height = 'auto';
    if (inputRef.current) {
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  // Build system message with curriculum context
  const buildMessages = useCallback(
    (userContent: string): ChatMessage[] => {
      let systemContent = `You are an AI tutor for the curriculum "Understanding Reality's Architecture." 
You are knowledgeable in philosophy, cognitive science, political economy, systems thinking, sociology, complexity science, and contemplative traditions.

Your role: Help the student deepen their understanding. Ask thought-provoking questions. 
Never give full answers to the Zeigarnik Hooks - those are meant to remain unresolved questions.
Encourage reflection rather than providing definitive answers. Use precise terminology but explain it.
Keep responses concise (2-4 paragraphs max) and end with a question to continue the dialogue.`;

      if (lessonContext?.lesson) {
        systemContent += `\n\nThe student is currently on:\nLesson ${lessonContext.lesson.number}: "${lessonContext.lesson.title}"\nOriginators: ${lessonContext.lesson.originators}\nCore Claim: ${lessonContext.lesson.coreClaim}\n\nHelp them explore this concept deeply.`;
      }

      const msgs: ChatMessage[] = [{ role: 'system', content: systemContent }];

      // Add previous messages
      for (const m of messages.slice(-10)) {
        msgs.push({ role: m.role, content: m.content } as ChatMessage);
      }

      msgs.push({ role: 'user', content: userContent });
      return msgs;
    },
    [messages, lessonContext]
  );

  const handleSend = useCallback(async () => {
    if (!input.trim() || sending || !selectedModel) return;

    const userContent = input.trim();
    setInput('');
    clearError();

    const userMsg: DisplayMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: userContent,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setSending(true);

    try {
      let streamedContent = '';
      await sendStreamingMessage(
        buildMessages(userContent),
        selectedModel,
        (chunk) => {
          streamedContent += chunk;
          setStreaming(streamedContent);
        }
      );

      if (streamedContent.trim()) {
        const assistantMsg: DisplayMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: streamedContent,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMsg]);
      }
    } catch {
      // Error handled by useShakespeare hook
    } finally {
      setStreaming('');
      setSending(false);
    }
  }, [input, sending, selectedModel, clearError, sendStreamingMessage, buildMessages]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex h-screen flex-col bg-slate-50 dark:bg-slate-950">
      {/* Chat Header */}
      <header className="border-b bg-white dark:bg-slate-900">
        <div className="container flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <Link
              to={lessonContext ? `/module/${moduleId}/lesson/${lessonId}` : '/'}
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              <ArrowLeft className="size-3.5" />
              {lessonContext ? 'Back to Lesson' : 'Dashboard'}
            </Link>
            {lessonContext?.lesson && (
              <>
                <span className="text-slate-300 dark:text-slate-700">|</span>
                <span className="truncate text-sm font-medium text-slate-700 dark:text-slate-300">
                  {lessonContext.lesson.number}: {lessonContext.lesson.title}
                </span>
              </>
            )}
          </div>
          <ModelSelector onSelect={setSelectedModel} selected={selectedModel} />
        </div>
      </header>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="container py-6">
          <div className="mx-auto max-w-2xl space-y-4">
            {/* Welcome / Empty State */}
            {messages.length === 0 && !streaming && (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
                    <BookOpen className="size-7 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
                    Your AI Curriculum Guide
                  </h2>
                  <p className="mx-auto max-w-sm text-sm text-slate-600 dark:text-slate-400">
                    {lessonContext?.lesson
                      ? `Ask questions about "${lessonContext.lesson.title}" — dive deeper into ${lessonContext.lesson.originators}'s ideas, explore the conceptual tension, or ask for help with the micro-praxis.`
                      : 'Ask me anything about the curriculum. Explore concepts, debate tensions, or get help applying frameworks to your own life.'}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Messages */}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  'flex gap-3',
                  msg.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {msg.role === 'assistant' && (
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
                    <Bot className="size-4 text-purple-600 dark:text-purple-400" />
                  </div>
                )}
                <div
                  className={cn(
                    'max-w-[80%] rounded-xl px-4 py-3 text-sm leading-relaxed',
                    msg.role === 'user'
                      ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                      : 'bg-white border text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300'
                  )}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
                {msg.role === 'user' && (
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-800">
                    <User className="size-4 text-slate-600 dark:text-slate-400" />
                  </div>
                )}
              </div>
            ))}

            {/* Streaming message */}
            {streaming && (
              <div className="flex gap-3">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
                  <Bot className="size-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="max-w-[80%] rounded-xl border bg-white px-4 py-3 text-sm leading-relaxed text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                  <p className="whitespace-pre-wrap">{streaming}</p>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
                <CardContent className="flex items-center gap-2 py-3">
                  <AlertCircle className="size-4 text-red-600 dark:text-red-400" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </CardContent>
              </Card>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Input Area */}
      <div className="border-t bg-white dark:bg-slate-900">
        <div className="container py-4">
          <div className="mx-auto max-w-2xl">
            {!isAuthenticated ? (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <Sparkles className="mx-auto mb-2 size-6 text-purple-500" />
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Log in with Nostr to use the AI Tutor
                  </p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Your Nostr account authenticates you with Shakespeare AI
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="flex items-end gap-2">
                <Textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    lessonContext?.lesson
                      ? 'Ask a question about this concept...'
                      : 'Ask anything about the curriculum...'
                  }
                  disabled={sending || !selectedModel}
                  rows={1}
                  className="min-h-[44px] resize-none rounded-xl bg-slate-50 text-sm dark:bg-slate-800"
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || sending || !selectedModel}
                  size="icon"
                  className="size-11 shrink-0 rounded-xl bg-purple-600 hover:bg-purple-700"
                >
                  {sending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Send className="size-4" />
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
